/**
 * useTaskFiltering Hook
 *
 * Handles filtering, sorting, and computed properties for tasks.
 * Accepts the current user's Practitioner reference so it no longer depends on mock data.
 */

import type { Task } from '@medplum/fhirtypes';
import { TASK_LIFECYCLE_TIMED_RELEASE_CODE } from '@unify/fhir-model/constants';
import { hasTaskLifecycleTag } from '@unify/fhir-model/resources/task';
import { useMemo } from 'react';
import { getTaskDisplayDescription } from '../../../lib/portalAppointmentTaskDisplay';
import { getTaskTypeCoding } from '../services/taskFhirMapping';
import { taskMatchesInboxOwnerFilter } from '../taskInboxScope';
import type { TaskFilters, TaskPriority, TaskWithUI } from '../types';
import { TASK_PRIORITY_RANK, toInboxStatus } from '../types';
import { parseFhirDateOrInstant } from '@/lib/dateHelpers';

interface UseTaskFilteringResult {
  filteredTasks: TaskWithUI[];
  activeTaskCount: number;
  teamTaskCount: number;
  overdueCount: number;
}

/** Local midnight of the due civil day (date-only due dates are not shifted through UTC). */
function dueDayStart(dateStr: string | undefined): Date | null {
  const d = parseFhirDateOrInstant(dateStr);
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * A Task `task-timer` has not released yet (`on-hold` + `timed-release`). It is not work to do, so the inbox hides it
 * in every view; a plain `on-hold` Task a user parked stays visible.
 */
export function isUnreleasedTimedTask(task: Task): boolean {
  return task.status === 'on-hold' && hasTaskLifecycleTag(task, TASK_LIFECYCLE_TIMED_RELEASE_CODE);
}

function isOverdue(dateStr: string | undefined): boolean {
  const dueDate = dueDayStart(dateStr);
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
}

function isDueToday(dateStr: string | undefined): boolean {
  const dueDate = dueDayStart(dateStr);
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate.getTime() === today.getTime();
}

function isDueThisWeek(dateStr: string | undefined): boolean {
  const dueDate = dueDayStart(dateStr);
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weekFromNow = new Date(today);
  weekFromNow.setDate(weekFromNow.getDate() + 7);
  return dueDate >= today && dueDate <= weekFromNow;
}

function getDueDateSortKey(dateStr: string | undefined): number | null {
  return dueDayStart(dateStr)?.getTime() ?? null;
}

function enhanceTask(task: Task): TaskWithUI {
  const dueDate = task.restriction?.period?.end;
  return {
    ...task,
    isOverdue: isOverdue(dueDate),
    isDueToday: isDueToday(dueDate),
    isDueThisWeek: isDueThisWeek(dueDate),
    assigneeDisplayName: task.owner?.display,
    patientDisplayName: task.for?.display,
  };
}

export function useTaskFiltering(
  tasks: Task[],
  filters: TaskFilters,
  currentUserRef: string | undefined,
  allowedOwnerRefs: ReadonlySet<string>,
  anyOrganizationOwner = false,
): UseTaskFilteringResult {
  return useMemo(() => {
    const enhancedTasks = tasks.filter((task) => !isUnreleasedTimedTask(task)).map(enhanceTask);

    // Filter by provider (multi-select: My Tasks, My Team, or specific practitioner refs)
    let filtered = enhancedTasks.filter((task) => taskMatchesInboxOwnerFilter(
      task.owner?.reference,
      filters.provider,
      currentUserRef,
      allowedOwnerRefs,
      anyOrganizationOwner,
    ));

    // Filter by task type
    if (filters.taskType.length > 0) {
      filtered = filtered.filter((task) => {
        const code = getTaskTypeCoding(task)?.code;
        return code != null && filters.taskType.includes(code);
      });
    }

    // When no status filter is applied, hide completed and cancelled
    if (filters.status.length === 0) {
      filtered = filtered.filter(
        (task) => task.status !== 'completed' && task.status !== 'cancelled',
      );
    }

    const activeTaskCount = enhancedTasks.filter(
      (t) =>
        currentUserRef != null &&
        t.owner?.reference === currentUserRef &&
        t.status !== 'completed' &&
        t.status !== 'cancelled',
    ).length;

    const teamTaskCount = enhancedTasks.filter(
      (t) =>
        t.owner?.reference !== currentUserRef &&
        t.status !== 'completed' &&
        t.status !== 'cancelled',
    ).length;

    const overdueCount = filtered.filter(
      (t) => t.isOverdue && t.status !== 'completed' && t.status !== 'cancelled',
    ).length;

    // Filter by inbox status (Open includes leftover non-terminal FHIR statuses)
    if (filters.status.length > 0) {
      filtered = filtered.filter((task) => filters.status.includes(toInboxStatus(task.status)));
    }

    // Filter by assignee
    if (filters.provider.length === 0 && filters.assignee !== 'all') {
      filtered = filtered.filter((task) => task.owner?.reference === filters.assignee);
    }

    // Filter by overdue only
    if (filters.showOverdueOnly) {
      filtered = filtered.filter(
        (task) => task.isOverdue && task.status !== 'completed' && task.status !== 'cancelled',
      );
    }

    // Filter by search
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (task) =>
          getTaskDisplayDescription(task, 'provider').toLowerCase().includes(searchLower) ||
          task.description?.toLowerCase().includes(searchLower) ||
          task.owner?.display?.toLowerCase().includes(searchLower) ||
          task.for?.display?.toLowerCase().includes(searchLower) ||
          task.note?.[0]?.text?.toLowerCase().includes(searchLower),
      );
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (filters.sortBy) {
        case 'dueDate': {
          const keyA = getDueDateSortKey(a.restriction?.period?.end);
          const keyB = getDueDateSortKey(b.restriction?.period?.end);
          if (keyA == null && keyB == null) comparison = 0;
          else if (keyA == null) comparison = 1;
          else if (keyB == null) comparison = -1;
          else comparison = keyA - keyB;
          break;
        }
        case 'priority': {
          const priorityA = (a.priority as TaskPriority) || 'routine';
          const priorityB = (b.priority as TaskPriority) || 'routine';
          comparison = TASK_PRIORITY_RANK[priorityA] - TASK_PRIORITY_RANK[priorityB];
          break;
        }
        case 'status': {
          comparison = toInboxStatus(a.status).localeCompare(toInboxStatus(b.status));
          break;
        }
        case 'created': {
          const createdA = a.authoredOn || '';
          const createdB = b.authoredOn || '';
          comparison = new Date(createdB).getTime() - new Date(createdA).getTime();
          break;
        }
      }

      return filters.sortDirection === 'asc' ? comparison : -comparison;
    });

    return {
      filteredTasks: filtered,
      activeTaskCount,
      teamTaskCount,
      overdueCount,
    };
  }, [tasks, filters, currentUserRef, allowedOwnerRefs, anyOrganizationOwner]);
}
